import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { AxiosRequestConfig } from 'axios';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { reportEvent } from 'Shared/analytics';
import { Column, LoadingPage, Row, useToast } from 'Shared/components';
import { isMissing, isPresent } from 'Shared/utils';
import {
  AgencyBundle,
  CoveragesList,
  EffectiveDate,
  ScreenHeader,
  DriversList,
  VehiclesList,
  RoutePrompt,
  VehicleDriverAssignment,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  Body,
  TelematicsEnrollment,
} from 'QuoteApp/components';
import { VehicleMultiselectProvider } from 'QuoteApp/components/VehicleMultiselect/useVehicleMultiselect';
import { useFooter, useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import {
  buildCoveragesByFieldValue,
  driverValidation,
  formatDriverDisplay,
  formatVehicleDisplay,
  getAddressDataByFieldValue,
  getContactInfoByFieldValue,
  getDriverDataByFieldValue,
  getDriverDataCompleteness,
  getVehicleDataByFieldValue,
  getVehicleDataCompleteness,
  vehicleValidation,
  getVehicleError,
  addressValidation,
  contactInfoValidation,
  getErrorMessage,
  createStackedCoverageMap,
} from 'QuoteApp/utils';
import { EditProvider, FieldDataContext } from 'QuoteApp/utils/contexts';
import {
  FormValuesType,
  GetDetailsResponseType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  transformEntitiesRequest,
  transformSavePolicyContactInfo,
} from 'QuoteApp/services/endpoints/PostSaveSummary';
import { useAppContext } from 'QuoteApp/services/providers/AppProvider';
import { OutdatedAppDataModal } from './OutdatedAppDataModal';
import { PolicyContactInfo } from './PolicyContactInfo';
import { SummaryAlertBanners } from './SummaryAlertBanners';
import { SummarySection } from './SummarySection';
import { SummaryDisclaimer } from './SummaryDisclaimer';
import { RewardsBanner } from 'QuoteApp/components/RewardsBanner';
import { CoverageUpdateModal } from 'Shared/components/CoverageUpdateModal';
import {
  useSaveCoverages,
  getHealedCoverages,
  getCoverageErrors,
} from 'QuoteApp/services/providers/AppProvider/hooks';
import { useErrorHandler } from 'react-error-boundary';
import { mergeDictionaryIntoCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useDictionary/utils';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useInitializeCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useInitializeCoverages';
import { useSetMinimumCoverages } from 'QuoteApp/services/providers/AppProvider/hooks/useSetMinimumCoverages';

// In the consumer flow, we ask the customer to confirm whether their mailing address matches their residency address.
// Since we don’t ask this question in the agent app,
// this command was introduced to perform that confirmation programmatically.
const CONFIRM_MAILING_ADDRESS_MATCHES_RESIDENCY_ADDRESS =
  'confirm_mailing_address_matches_residency_address';

export const mergeInProgressDataFromSummary = (
  currentDetails: GetDetailsResponseType,
  newDetails: GetDetailsResponseType
) => {
  return {
    ...newDetails,
    drivers: currentDetails.drivers,
    vehicles: currentDetails.vehicles,
    contactInfo: currentDetails.contactInfo,
    address: currentDetails.address,
    addressOutcomes: currentDetails.addressOutcomes,
  };
};

export const Summary: FC = () => {
  const { previousStep, nextStep } = useFooter(3);
  const { toast } = useToast();
  const {
    appId,
    config,
    details,
    dictionary,
    refreshConfig,
    refreshRate,
    setDetails,
    getRateOnLoad,
    refreshDetails,
    rate,
    api,
    rewardsEnrollmentResponse,
    isEstimatePointsLoading,
  } = useAppContext();

  const {
    runnable: shouldInitializeCoverages,
    perform: initializeCoverages,
  } = useInitializeCoverages();

  const {
    runnable: canSetMinimumCoverages,
    perform: setMinimumCoverages,
    pending: isSettingMinimumCoverages,
  } = useSetMinimumCoverages();

  const {
    selfReportedAccidentsForClassificationsFlag,
    collectDayOfAccidentFlag,
  } = useQuoteAppFeatureFlags();

  const handleError = useErrorHandler();

  const [, postConfirmAddressesMatch] = useLazyQuoteApi({
    method: 'POST',
    url: `/insurance_applications/${appId}/${CONFIRM_MAILING_ADDRESS_MATCHES_RESIDENCY_ADDRESS}`,
  });

  const handleNextStep = async () => {
    const confirmCommand = details.commands.confirmAddressesMatch;

    if (confirmCommand?.runnable) {
      await postConfirmAddressesMatch();
    }
    nextStep.onClickSuccess();
  };

  const saveCoverages = useSaveCoverages();

  useEffect(() => {
    getRateOnLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Need to run details refresh on rate refresh to enable awards to refresh
    if (
      rewardsEnrollmentResponse?.isEnrolled &&
      rate &&
      !isEstimatePointsLoading
    ) {
      refreshDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, rewardsEnrollmentResponse]);

  const addressEditingId = `${details.principalInsuredId}-address`;
  const contactInfoEditingId = `${details.principalInsuredId}-contact-info`;
  const mailingAddressEditingId = `${details.principalInsuredId}-mailing-address`;

  const serverErrors = useMemo(
    () => ({
      drivers: getDriverDataByFieldValue(details.drivers, 'errors'),
      vehicles: getVehicleDataByFieldValue(details.vehicles, 'errors'),
      contactInfo: getContactInfoByFieldValue(details.contactInfo, 'errors'),
    }),
    [details.drivers, details.vehicles, details.contactInfo]
  );

  const editableFieldData = useMemo(
    () => ({
      drivers: getDriverDataByFieldValue(details.drivers, 'editable'),
      vehicles: getVehicleDataByFieldValue(details.vehicles, 'editable'),
      contactInfo: getContactInfoByFieldValue(details.contactInfo, 'editable'),
    }),
    [details.drivers, details.vehicles, details.contactInfo]
  );

  const driversDataCompleteness = useMemo(
    () => getDriverDataCompleteness(serverErrors.drivers),
    [serverErrors.drivers]
  );

  const vehicleDataCompleteness = useMemo(
    () => getVehicleDataCompleteness(serverErrors.vehicles),
    [serverErrors.vehicles]
  );

  const driverFormInitialValues = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'value'),
    [details.drivers]
  );
  const vehicleFormInitialValues = useMemo(
    () => getVehicleDataByFieldValue(details.vehicles, 'value'),
    [details.vehicles]
  );

  const driverDataFieldsMasked = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'masked'),
    [details.drivers]
  );

  // used for save modals to display the name of the entity in edit mode
  const formatEditingId = useCallback(
    (editingId: string | null) => {
      if (isMissing(editingId) || isMissing(config)) {
        return '';
      } else if (isPresent(driverFormInitialValues[editingId])) {
        return formatDriverDisplay(driverFormInitialValues[editingId], config);
      } else if (isPresent(vehicleFormInitialValues[editingId])) {
        return formatVehicleDisplay(vehicleFormInitialValues[editingId]);
      } else if (editingId === addressEditingId) {
        return 'the policy address';
      } else if (editingId === mailingAddressEditingId) {
        return 'the policy mailing address';
      } else if (editingId === contactInfoEditingId) {
        return 'the policy contact information';
      }
      return ''; // this should never happen
    },
    [
      addressEditingId,
      mailingAddressEditingId,
      config,
      contactInfoEditingId,
      driverFormInitialValues,
      vehicleFormInitialValues,
    ]
  );

  const getValidationSchema = useCallback(
    (editingId: string, values: FormValuesType) => {
      if (Object.keys(values.drivers).includes(editingId)) {
        return Yup.object().shape({
          drivers: Yup.object().shape({
            [editingId]: driverValidation(
              values.drivers[editingId],
              selfReportedAccidentsForClassificationsFlag,
              collectDayOfAccidentFlag
            ),
          }),
        });
      }

      if (Object.keys(values.vehicles).includes(editingId)) {
        return Yup.object().shape({
          vehicles: Yup.object().shape({
            [editingId]: vehicleValidation,
          }),
        });
      }

      if (editingId === addressEditingId) {
        return Yup.object().shape({
          address: addressValidation(details.principalInsuredId),
        });
      }

      if (editingId === mailingAddressEditingId) {
        return Yup.object().shape({
          mailingAddress: addressValidation(details.principalInsuredId),
        });
      }

      if (editingId === contactInfoEditingId) {
        return Yup.object().shape({
          contactInfo: contactInfoValidation(details.principalInsuredId),
        });
      }

      return null;
    },
    [
      addressEditingId,
      mailingAddressEditingId,
      contactInfoEditingId,
      details.principalInsuredId,
      selfReportedAccidentsForClassificationsFlag,
      collectDayOfAccidentFlag,
    ]
  );

  useEffect(() => {
    if (shouldInitializeCoverages) {
      initializeCoverages()
        .then(() => refreshRate())
        .catch(() =>
          handleError({
            name: 'initializeCoveragesError',
            message: 'Something went wrong while initializing coverages.',
          })
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldInitializeCoverages]);

  if (!config) {
    return <LoadingPage />;
  }

  const { attributes } =
    details.eligibleDiscounts?.find(
      discount => discount.type === 'futureEffectiveDate'
    ) || {};
  const dayLabel =
    attributes?.startDaysOffset && attributes?.startDaysOffset > 1
      ? 'days'
      : 'day';

  const effectiveDateText = attributes?.startDaysOffset
    ? `Eligible customers who delay their start date by at least ${attributes?.startDaysOffset} ${dayLabel} can get a discount.`
    : 'Please select an effective date for this policy.';

  // Create a map of stacked coverages organized by their parent coverage
  const stackedCoverages = createStackedCoverageMap(
    config.coverages.insuranceProduct
  );

  return (
    <>
      <OutdatedAppDataModal />
      <Body data-testid="summary-page">
        {rewardsEnrollmentResponse?.isEnrolled && <RewardsBanner />}
        <ScreenHeader
          headerText="Quote Summary"
          subHeaderText={
            "Please review and complete the fields below and make sure that all information is accurate. On the next step, we'll run final reports and update\u00a0the\u00a0quote."
          }
        />
        <SummaryAlertBanners />
        <FieldDataContext.Provider
          value={{
            serverErrors,
            editableFields: editableFieldData,
            alwaysShowErrors: true,
          }}
        >
          <EditProvider>
            {({ editingId, changeEditingId }) => (
              /*
                This top-level form handles saving drivers, vehicles, policy address, and policy contact info.
                Everything must be contained in this form so the scope for which entities are dirty can be shared
                and changes can be saved or discarded if needed on route change and editingId change. Any future accordions
                added to the Summary page should also be handled by this form.

                Other sections of this page including coverages, discounts, vehicle driver assignment and effective date
                are nested inside this form for page layout/style reasons, but handle their own form state/save functions.
              */
              <VehicleMultiselectProvider>
                {({ isEditingMultiple }) => (
                  <Formik
                    enableReinitialize
                    validate={values => {
                      if (!isPresent(editingId)) {
                        throw new Error('Failed to validate');
                      }
                      const schema = getValidationSchema(editingId, values);
                      try {
                        validateYupSchema(values, schema, true, values);
                        return {};
                      } catch (err) {
                        return yupToFormErrors(err);
                      }
                    }}
                    initialValues={{
                      drivers: driverFormInitialValues,
                      vehicles: vehicleFormInitialValues,
                      primaryDriverId: details.principalInsuredId,
                      contactInfo: getContactInfoByFieldValue(
                        details.contactInfo,
                        'value'
                      ),
                      address: getAddressDataByFieldValue(
                        details.address,
                        'value'
                      ),
                      mailingAddress: getAddressDataByFieldValue(
                        details.mailingAddress,
                        'value'
                      ),
                    }}
                    initialStatus={{
                      isInvalidAddress:
                        !details.addressOutcomes.isAddressValid &&
                        !details.addressOutcomes.isMissingLineTwo,
                      isMissingLineTwo:
                        details.addressOutcomes.isMissingLineTwo,
                      isInvalidMailingAddress:
                        !details.mailingAddressOutcomes.isAddressValid &&
                        !details.mailingAddressOutcomes.isMissingLineTwo,
                      isMissingMailingLineTwo:
                        details.mailingAddressOutcomes.isMissingLineTwo,
                    }}
                    onSubmit={async (
                      values,
                      { setSubmitting, setFieldError, resetForm }
                    ) => {
                      try {
                        if (!isPresent(editingId)) {
                          throw new Error('Failed to edit');
                        }

                        const isEditingDriver = Object.keys(
                          values.drivers
                        ).includes(editingId);
                        const isEditingVehicle = Object.keys(
                          values.vehicles
                        ).includes(editingId);

                        if (isEditingVehicle) {
                          /*  Handle post-submission vehicle form errors */
                          const postSubmissionVehicleError = getVehicleError(
                            values.vehicles[editingId],
                            isEditingMultiple
                          );
                          if (postSubmissionVehicleError) {
                            setFieldError(
                              `vehicles.${editingId}.submissionError`,
                              postSubmissionVehicleError
                            );
                            throw new Error(
                              'Please fix vehicle error before continuing.'
                            );
                          }
                        }

                        let axiosConfig: AxiosRequestConfig | undefined;

                        if (isEditingDriver || isEditingVehicle) {
                          /* Save drivers and vehicles */
                          axiosConfig = {
                            data: {
                              values,
                              initialValues: {
                                drivers: driverFormInitialValues,
                              },
                              editableFields: editableFieldData,
                              maskedFields: {
                                drivers: driverDataFieldsMasked,
                              },
                            },
                            transformRequest: transformEntitiesRequest,
                          };
                        } else if (
                          editingId === addressEditingId ||
                          editingId === mailingAddressEditingId ||
                          editingId === contactInfoEditingId
                        ) {
                          /* Save address, mailing address and contact information */
                          axiosConfig = {
                            data: values,
                            transformRequest: transformSavePolicyContactInfo,
                          };
                        }

                        if (!axiosConfig) {
                          throw new Error('Failed to edit.');
                        }

                        const response = await api.saveAppData(axiosConfig, {
                          suppressDefaultToastError: true,
                        });

                        if (isEditingDriver) {
                          await refreshConfig();
                        }

                        setDetails(response.data);

                        // Reset form to synchronously update initialValues and clear dirty state
                        // This prevents race condition where enableReinitialize (async) hasn't finished
                        // when navigation is attempted, causing RoutePrompt to block unnecessarily
                        // Only reset the fields that were actually saved (drivers/vehicles) to preserve
                        // unsaved changes in other sections (contactInfo/address/mailingAddress)
                        // Note: getDriverDataByFieldValue already includes masked license numbers from server
                        if (isEditingVehicle || isEditingDriver) {
                          resetForm({
                            values: {
                              // Preserve unsaved changes in other sections from current form values
                              contactInfo: values.contactInfo,
                              address: values.address,
                              mailingAddress: values.mailingAddress,
                              // Update only the sections that were saved from server response
                              drivers: getDriverDataByFieldValue(
                                response.data.drivers,
                                'value'
                              ),
                              vehicles: getVehicleDataByFieldValue(
                                response.data.vehicles,
                                'value'
                              ),
                              primaryDriverId: response.data.principalInsuredId,
                            },
                          });
                        }

                        changeEditingId(null);
                        refreshRate();

                        if (editingId === addressEditingId) {
                          if (
                            !response.data.addressOutcomes.isAddressValid &&
                            !response.data.addressOutcomes.isMissingLineTwo
                          ) {
                            reportEvent(
                              'aqui-invalid-address-notification-displayed-summary'
                            );
                            throw new Error('Invalid address entered.');
                          }
                        }

                        if (
                          editingId === mailingAddressEditingId &&
                          !response.data.mailingAddressOutcomes
                            .isAddressValid &&
                          !response.data.mailingAddressOutcomes.isMissingLineTwo
                        ) {
                          reportEvent(
                            'aqui-invalid-mailing-address-notification-displayed-summary'
                          );
                          throw new Error('Invalid mailing address entered.');
                        }
                      } catch (e) {
                        toast({
                          type: 'error',
                          message: getErrorMessage(e),
                          withSecondaryNav: false,
                          icon: 'error',
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ dirty, isValid, isSubmitting }) => (
                      <>
                        <RoutePrompt
                          when={dirty && !isSubmitting}
                          editingIdDisplay={formatEditingId(editingId)}
                          isValid={isValid}
                        />
                        <SummarySection headerText="Drivers">
                          <DriversList
                            dataCompleteness={driversDataCompleteness}
                            editingIdDisplay={formatEditingId(editingId)}
                          />
                        </SummarySection>
                        <SummarySection headerText="Vehicles">
                          <VehiclesList
                            dataCompleteness={vehicleDataCompleteness}
                            editingIdDisplay={formatEditingId(editingId)}
                          />
                        </SummarySection>

                        <SummarySection
                          headerText="Coverages"
                          headerButton={
                            canSetMinimumCoverages
                              ? {
                                  text: 'Set minimum limits',
                                  onClick: () => setMinimumCoverages(),
                                  disabled: isSettingMinimumCoverages,
                                }
                              : undefined
                          }
                        >
                          <Formik
                            enableReinitialize
                            initialValues={{
                              coverages: buildCoveragesByFieldValue(
                                details.coverages,
                                'value'
                              ),
                            }}
                            initialStatus={{
                              healing: {
                                coverages: getHealedCoverages(
                                  details.coverages
                                ),
                              },
                              errors: {
                                coverages: getCoverageErrors(details.coverages),
                              },
                            }}
                            onSubmit={(data, actions) =>
                              saveCoverages(data, actions)
                            }
                          >
                            <CoveragesList
                              config={mergeDictionaryIntoCoverages(
                                config?.coverages,
                                dictionary?.coverages
                              )}
                              details={details}
                              stackedCoverages={stackedCoverages}
                            />
                          </Formik>
                        </SummarySection>

                        {details.vehicleDriverAssignment !== null && (
                          <VehicleDriverAssignment
                            editable={
                              details.drivers[details.principalInsuredId]
                                .editable
                            }
                          />
                        )}

                        <SummarySection
                          headerText="Policy Contact Info"
                          subHeaderText="We won't contact the customer until a policy has been bound.
                  We'll use the email provided here to send all eSign documents,
                  ID cards, mobile app activation, and other important notices."
                        >
                          <PolicyContactInfo
                            addressEditingId={addressEditingId}
                            mailingAddressEditingId={mailingAddressEditingId}
                            contactInfoEditingId={contactInfoEditingId}
                            editingIdDisplay={formatEditingId(editingId)}
                          />
                        </SummarySection>

                        <AgencyBundle />

                        <TelematicsEnrollment />

                        <SummarySection
                          headerText="Effective Date"
                          subHeaderText={effectiveDateText}
                        >
                          <Row>
                            <Column variant="third">
                              <EffectiveDate />
                            </Column>
                          </Row>
                        </SummarySection>
                      </>
                    )}
                  </Formik>
                )}
              </VehicleMultiselectProvider>
            )}
          </EditProvider>
        </FieldDataContext.Provider>
        <SummaryDisclaimer />
      </Body>
      <Footer
        previousButton={
          <FooterPreviousButton onClick={previousStep.onClickSuccess}>
            {previousStep.text}
          </FooterPreviousButton>
        }
        nextButton={
          <FooterNextButton onClick={handleNextStep}>
            {nextStep.text}
          </FooterNextButton>
        }
        showRewardsEarned={true}
      />
      <CoverageUpdateModal open={details.quoteChangeAcknowledgement} />
    </>
  );
};
